import { IAvatarChatHistoryMessage } from "@components/InteractiveChat/components/InteractiveChatHistory";
import { AvatarChatHistoryMessageSource } from "@components/InteractiveChat/components/InteractiveChatHistory";
import ApiRoutes from "@services/routing/ApiRoutes";
import { createValidDate, toSafeISOString } from "@services/utils/dateUtils";
import {
  Conversation,
  ConversationHistory,
  ConversationMessage
} from "./models";

export class AuthenticationError extends Error {
  translationKey: string;

  constructor(translationKey: string = "chatbot.errors.session_expired") {
    super(translationKey);
    this.name = "AuthenticationError";
    this.translationKey = translationKey;
  }
}

class ConversationService {
  /**
   * Get list of conversations for the current user
   * @param userId - User ID (optional, can be extracted from context)
   * @returns Promise with list of conversations
   */
  async getConversations(userId?: string): Promise<Conversation[]> {
    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      const response = await fetch(ApiRoutes.getThreads(userId), {
        method: "GET",
        headers: {
          ...ApiRoutes.apiHeaderKey(),
          "Content-Type": "application/json"
        },
        credentials: "include",
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // If endpoint doesn't exist (404) or is not implemented (501), return empty array silently
        if (response.status === 404 || response.status === 501) {
          return [];
        }
        if (response.status === 401) {
          throw new AuthenticationError("chatbot.errors.session_expired");
        }
        // For other errors, return empty array silently (endpoint might not be ready)
        return [];
      }

      const data: {
        user_id: number;
        threads: Array<{
          id: string;
          timestamp: string;
          title: string;
        }>;
      } = await response.json();

      const conversations: Conversation[] = data.threads.map((threadInfo) => {
        const timestamp = createValidDate(threadInfo.timestamp);

        return {
          id: threadInfo.id,
          thread_id: threadInfo.id,
          user_id: userId || data.user_id.toString(),
          title:
            threadInfo.title.length > 100
              ? threadInfo.title.substring(0, 100) + "..."
              : threadInfo.title,
          timestamp: timestamp,
          preview: "", // Preview not provided by backend, not used in UI
          created_at: toSafeISOString(timestamp),
          updated_at: toSafeISOString(timestamp)
        };
      });

      return conversations.sort((a, b) => {
        const timeDiff = b.timestamp.getTime() - a.timestamp.getTime();
        if (timeDiff !== 0) {
          return timeDiff;
        }
        return a.thread_id.localeCompare(b.thread_id);
      });
    } catch (error) {
      // Silently handle errors - endpoint might not be available yet
      // Only log if it's not an abort (timeout) or network error
      if (error instanceof Error) {
        if (error.name === "AbortError") {
          // Timeout - endpoint not responding, silently return empty
          return [];
        }
        if (error instanceof AuthenticationError) {
          // Re-throw authentication errors so they can be handled upstream
          throw error;
        }
      }
      // For all other errors (network, etc.), return empty array silently
      return [];
    }
  }

  /**
   * Get conversation history by thread_id
   * @param threadId - Thread ID
   * @param userId - User ID (optional, can be extracted from context)
   * @returns Promise with conversation history
   */
  async getConversationHistory(
    threadId: string,
    userId?: string
  ): Promise<ConversationHistory> {
    try {
      const response = await fetch(
        ApiRoutes.getConversationHistory(threadId, userId),
        {
          method: "GET",
          headers: {
            ...ApiRoutes.apiHeaderKey(),
            "Content-Type": "application/json"
          },
          credentials: "include"
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new AuthenticationError("chatbot.errors.session_expired");
        }
        throw new Error("chatbot.errors.fetch_conversation_history_error");
      }

      const backendData: {
        thread_id: string;
        messages: Array<{
          role: string; // "user" or "assistant"
          content: string;
          timestamp: string; // ISO format
          sources?: string[] | null; // Array of source IDs
        }>;
      } = await response.json();

      const mappedMessages: ConversationMessage[] = backendData.messages.map(
        (msg) => {
          // Validate timestamp before creating date
          const timestamp = msg.timestamp || "";
          const validDate = createValidDate(timestamp);

          return {
            source: msg.role === "user" ? "user" : "avatar",
            text: msg.content,
            timestamp: validDate,
            isComplete: true,
            sources:
              msg.sources && msg.sources.length > 0 ? msg.sources : undefined
          };
        }
      );

      const finalUserId = userId || "unknown";

      // Get first and last message timestamps safely
      const firstMessage = mappedMessages.length > 0 ? mappedMessages[0] : null;
      const lastMessage =
        mappedMessages.length > 0
          ? mappedMessages[mappedMessages.length - 1]
          : null;

      // Validate timestamps before using them
      const firstTimestamp = firstMessage?.timestamp;
      const lastTimestamp = lastMessage?.timestamp;

      return {
        thread_id: backendData.thread_id,
        user_id: finalUserId,
        messages: mappedMessages,
        created_at: toSafeISOString(firstTimestamp),
        updated_at: toSafeISOString(lastTimestamp)
      };
    } catch (error) {
      console.error("Error fetching conversation history:", error);
      // Return empty history on error
      return {
        thread_id: threadId,
        user_id: userId || "unknown",
        messages: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    }
  }

  /**
   * Convert ConversationHistory to IAvatarChatHistoryMessage[]
   * @param history - Conversation history from backend
   * @returns Array of chat history messages
   */
  convertToChatHistory(
    history: ConversationHistory
  ): IAvatarChatHistoryMessage[] {
    return history.messages.map((msg) => {
      // Handle Date or string timestamp
      let validDate: Date;
      if (msg.timestamp instanceof Date) {
        validDate = msg.timestamp;
      } else {
        validDate = createValidDate(msg.timestamp);
      }

      return {
        source:
          msg.source === "user"
            ? AvatarChatHistoryMessageSource.USER
            : AvatarChatHistoryMessageSource.AVATAR,
        text: msg.text,
        timestamp: validDate,
        isComplete: msg.isComplete ?? true,
        sources: msg.sources
      };
    });
  }
}

export const conversationService = new ConversationService();
export default conversationService;
